import { useState, type FC } from 'react'

import type { BaseResponseError } from '@skytech/auth'
import { Checkbox, MessagePopup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Input } from '@skytech/ui/Input'
import { useMutation } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { API_ROUTE_WORKFLOWS_DUNNING_RUN } from '@/resources/api-routes'
import { post } from '@/util/auth'

type RunDunningParams = {
  customerId?: number
  maxAgeInMonths?: number
  forceSend: boolean
}

type Props = {
  isOpen: boolean
  onClose: () => void
}

export const RunWorkflowModal: FC<Props> = ({ isOpen, onClose }) => {
  const [customerId, setCustomerId] = useState<string>('')
  const [maxAgeInMonths, setMaxAgeInMonths] = useState<string>('6')
  const [forceSend, setForceSend] = useState<boolean>(true)
  const [conflictError, setConflictError] = useState<boolean>(false)

  const mutation = useMutation(
    (params: RunDunningParams) => post(API_ROUTE_WORKFLOWS_DUNNING_RUN, params, true),
    {
      onSuccess: () => {
        handleClose()
      },
      onError: (err: BaseResponseError) => {
        if (err.status !== 409) {
          // non-conflict errors are shown via mutation.error
        }
        setConflictError(err.status === 409)
      },
    }
  )

  const handleClose = () => {
    setCustomerId('')
    setMaxAgeInMonths('6')
    setForceSend(true)
    setConflictError(false)
    mutation.reset()
    onClose()
  }

  const handleRun = () => {
    setConflictError(false)
    const params: RunDunningParams = { forceSend }

    const parsedCustomerId = customerId.trim() ? parseInt(customerId, 10) : undefined
    if (parsedCustomerId && parsedCustomerId > 0) params.customerId = parsedCustomerId

    const parsedMaxAge = maxAgeInMonths.trim() ? parseInt(maxAgeInMonths, 10) : undefined
    if (parsedMaxAge && parsedMaxAge > 0) params.maxAgeInMonths = parsedMaxAge

    mutation.mutate(params)
  }

  return (
    <MessagePopup
      title={<Translation id="Mobile Asset Return Dunning" />}
      isOpen={isOpen}
      onClose={handleClose}
      type="warning"
      variant="medium"
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-500">
          <Translation id="Triggers the dunning workflow. Runs asynchronously — the endpoint returns as soon as Temporal accepts the trigger. Does not run on weekends (weekend guard is inside the workflow itself)." />
        </p>

        <Input
          id="customerId"
          type="number"
          label="Customer ID (optional — leave blank to run for all customers)"
          value={customerId}
          onChange={e => setCustomerId(e.target.value)}
          isFullWidth
        />

        <Input
          id="maxAgeInMonths"
          type="number"
          label="Max age in months (optional — defaults to 6)"
          value={maxAgeInMonths}
          onChange={e => setMaxAgeInMonths(e.target.value)}
          isFullWidth
        />

        <Checkbox
          id="forceSend"
          checked={forceSend}
          onChange={e => setForceSend(e.target.checked)}
          label="Force send — bypasses per-asset idempotency check (recommended for manual testing)"
        />

        {conflictError && (
          <p className="text-sm text-yellow-700">
            <Translation id="Workflow is already running. Wait for the current run to finish." />
          </p>
        )}

        {mutation.isError && !conflictError && (
          <p className="text-sm text-red-600">
            <Translation id="Failed to start workflow. Check the logs for details." />
          </p>
        )}
      </div>

      <footer className="mt-6 flex justify-end gap-2">
        <Button variant="tertiary" onClick={handleClose}>
          <Translation id="Cancel" />
        </Button>
        <Button variant="primary" isLoading={mutation.isLoading} onClick={handleRun}>
          <Translation id="Run workflow" />
        </Button>
      </footer>
    </MessagePopup>
  )
}
